import { useState, useEffect } from 'react';
import './App.css';
import { t, LANGUAGES } from './translations';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfUse from './TermsOfUse';
import SitePrivacy from './SitePrivacy';
import SiteTerms from './SiteTerms';
import AwardSpotlight from './AwardSpotlight';
import {
  ShieldCheck,
  EyeOff,
  GraduationCap,
  Star,
  ChevronRight,
  PlayCircle,
  Menu,
  X,
  Sparkles,
  Users,
  Check,
  Heart,
  Zap,
  Moon,
  Sun,
  Globe,
  Palette,
  FlaskConical,
} from 'lucide-react';

/* ============================
   EAGLETAP — Landing Page
   i18n + Dark Mode
   ============================ */

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [page, setPage] = useState(() => {
    const hash = window.location.hash;
    if (hash === '#/privacy') return 'privacy';
    if (hash === '#/terms') return 'terms';
    if (hash === '#/site-privacy') return 'site-privacy';
    if (hash === '#/site-terms') return 'site-terms';
    return 'home';
  });
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem('eagletap-lang');
    return saved || 'en';
  });
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('eagletap-dark');
    if (saved !== null) return saved === 'true';
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches || false;
  });
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [playVideo, setPlayVideo] = useState(false);

  // Persist preferences
  useEffect(() => {
    localStorage.setItem('eagletap-lang', lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    localStorage.setItem('eagletap-dark', darkMode);
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Hash routing (only for #/ routes, not anchors)
  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash;
      if (hash === '#/privacy') { setPage('privacy'); window.scrollTo(0, 0); }
      else if (hash === '#/terms') { setPage('terms'); window.scrollTo(0, 0); }
      else if (hash === '#/site-privacy') { setPage('site-privacy'); window.scrollTo(0, 0); }
      else if (hash === '#/site-terms') { setPage('site-terms'); window.scrollTo(0, 0); }
      // Don't interfere with normal anchor links like #supermath-teacher
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const goHome = () => {
    window.history.pushState(null, '', window.location.pathname);
    setPage('home');
    window.scrollTo(0, 0);
  };


  // Scroll listener for nav background
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // IntersectionObserver for scroll animations
  useEffect(() => {
    if (page !== 'home') return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    // Small delay to ensure DOM is ready after page switch
    const timer = setTimeout(() => {
      const elements = document.querySelectorAll('.animate-on-scroll');
      elements.forEach((el) => observer.observe(el));
    }, 50);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [page]);

  // Close lang menu on outside click
  useEffect(() => {
    if (!langMenuOpen) return;
    const handler = () => setLangMenuOpen(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [langMenuOpen]);

  const closeMenu = () => setIsMenuOpen(false);
  const currentFlag = LANGUAGES.find((l) => l.code === lang)?.flag || '🇬🇧';

  const scrollToSection = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.querySelectorAll('.animate-on-scroll').forEach(c => c.classList.add('visible'));
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const nav = t('nav', lang);
  const hero = t('hero', lang);
  const values = t('values', lang);
  const sm = t('supermath', lang);
  const stores = t('stores', lang);
  const smTeacher = t('supermathTeacher', lang);
  const leo = t('leonora', lang);
  const rnd = t('rnd', lang);
  const awards = t('awards', lang);
  const rev = t('reviews', lang);
  const foot = t('footer', lang);

  // Render legal pages
  if (page === 'privacy') return <PrivacyPolicy onBack={goHome} />;
  if (page === 'terms') return <TermsOfUse onBack={goHome} />;
  if (page === 'site-privacy') return <SitePrivacy onBack={goHome} />;
  if (page === 'site-terms') return <SiteTerms onBack={goHome} />;

  return (
    <div className="app">
      {/* ── NAVIGATION ── */}
      <nav className={`nav ${isScrolled ? 'scrolled' : ''}`}>
        <div className="nav-inner">
          <a href="#top" className="nav-logo">
            <img src={`${process.env.PUBLIC_URL}/logo_40.png`} alt="EagleTap" className="nav-logo-img" />
            <span className="nav-logo-text">EagleTap</span>
          </a>

          <div className="nav-links">
            <a href="#values" className="nav-link" onClick={(e) => scrollToSection(e, 'values')}>{nav.values}</a>
            <a href="#supermath" className="nav-link" onClick={(e) => scrollToSection(e, 'supermath')}>SuperMath</a>
            <a href="#awards" className="nav-link" onClick={(e) => scrollToSection(e, 'awards')}>{nav.awards}</a>
            <a href="#reviews" className="nav-link" onClick={(e) => scrollToSection(e, 'reviews')}>{nav.reviews}</a>
            {/* <a href="#teachers" className="nav-link">{nav.teachers}</a> */}

            {/* Language selector */}
            <div className="lang-selector" onClick={(e) => e.stopPropagation()}>
              <button
                className="lang-btn"
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                aria-label="Change language"
              >
                <Globe size={15} />
                <span className="lang-flag">{currentFlag}</span>
              </button>
              {langMenuOpen && (
                <div className="lang-dropdown">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      className={`lang-option ${lang === l.code ? 'active' : ''}`}
                      onClick={() => { setLang(l.code); setLangMenuOpen(false); }}
                    >
                      <span className="lang-option-flag">{l.flag}</span>
                      <span>{l.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Dark mode toggle */}
            <button
              className="dark-toggle"
              onClick={() => setDarkMode(!darkMode)}
              aria-label="Toggle dark mode"
            >
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <a href="#supermath" className="nav-cta">{nav.cta}</a>
          </div>

          <div className="nav-mobile-right">
            {/* Mobile language */}
            <div className="lang-selector" onClick={(e) => e.stopPropagation()}>
              <button
                className="lang-btn"
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                aria-label="Change language"
              >
                <span className="lang-flag">{currentFlag}</span>
              </button>
              {langMenuOpen && (
                <div className="lang-dropdown">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      className={`lang-option ${lang === l.code ? 'active' : ''}`}
                      onClick={() => { setLang(l.code); setLangMenuOpen(false); }}
                    >
                      <span className="lang-option-flag">{l.flag}</span>
                      <span>{l.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Mobile dark mode */}
            <button
              className="dark-toggle"
              onClick={() => setDarkMode(!darkMode)}
              aria-label="Toggle dark mode"
            >
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button
              className="nav-mobile-btn"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
        <a href="#values" className="nav-link" onClick={(e) => { closeMenu(); scrollToSection(e, 'values'); }}>{nav.values}</a>
        <a href="#supermath" className="nav-link" onClick={(e) => { closeMenu(); scrollToSection(e, 'supermath'); }}>SuperMath</a>
        <a href="#reviews" className="nav-link" onClick={(e) => { closeMenu(); scrollToSection(e, 'reviews'); }}>{nav.reviews}</a>
        {/* <a href="#teachers" className="nav-link" onClick={closeMenu}>{nav.teachers}</a> */}
        <a href="#supermath" className="nav-cta" onClick={(e) => { closeMenu(); scrollToSection(e, 'supermath'); }}>{nav.cta}</a>
      </div>

      {/* ── HERO ── */}
      <section id="top" className="hero">
        <div className="hero-bg">
          <video
            className="hero-video"
            autoPlay
            loop
            muted
            playsInline
            poster="/poster.jpg"
          >
            <source src="/video.mp4" type="video/mp4" />
          </video>
          <div className="hero-overlay" />
        </div>
        <div className="hero-content">
          <div className="hero-badge">
            <Sparkles size={16} />
            <span>{hero.badge}</span>
          </div>
          <h1 className="hero-title">
            {hero.titleLine1}
            <br />
            <span className="hero-title-accent">{hero.titleLine2}</span>
          </h1>
          <p className="hero-subtitle">{hero.subtitle}</p>
          <div className="hero-actions">
            <a href="#supermath" className="btn btn-primary btn-lg">
              {hero.cta1} <ChevronRight size={20} />
            </a>
            <a href="#values" className="btn btn-secondary btn-lg">
              {hero.cta2}
            </a>
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section id="values" className="section values">
        <div className="section-inner">
          <div className="section-header animate-on-scroll">
            <div className="section-label">
              <Heart size={14} />
              {values.label}
            </div>
            <h2 className="section-title">{values.title}</h2>
            <p className="section-subtitle">{values.subtitle}</p>
          </div>
          <div className="values-grid">
            <ValueCard
              iconClass="value-icon-privacy"
              icon={<ShieldCheck size={24} />}
              title={values.privacy}
              desc={values.privacyDesc}
              delay={1}
            />
            <ValueCard
              iconClass="value-icon-ads"
              icon={<EyeOff size={24} />}
              title={values.ads}
              desc={values.adsDesc}
              delay={2}
            />
            <ValueCard
              iconClass="value-icon-ethical"
              icon={<GraduationCap size={24} />}
              title={values.ethical}
              desc={values.ethicalDesc}
              delay={3}
            />
          </div>
        </div>
      </section>

      {/* ── SUPERMATH ── */}
      <section id="supermath" className="section supermath">
        <div className="section-inner">
          <div className="supermath-layout">
            <div className="supermath-content animate-on-scroll">
              <div className="supermath-badge">
                <Zap size={12} />
                {sm.badge}
              </div>
              <h2 className="supermath-title">SuperMath</h2>
              <p className="supermath-desc">{sm.desc}</p>
              <div className="supermath-rating">
                <div className="supermath-stars">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} fill="currentColor" size={18} />
                  ))}
                </div>
                <span className="supermath-rating-text">{sm.rating}</span>
              </div>
              <div className="supermath-actions">
                <a
                  href={`https://apps.apple.com/${lang === 'en' ? 'us' : lang}/app/supermath-kids-math-games/id1109968749`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-app-store"
                >
                  <PlayCircle size={20} />
                  {stores.appStore}
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.eagletap.mathematicalrun"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-app-store"
                >
                  <PlayCircle size={20} />
                  {stores.playStore}
                </a>
                <a
                  href="https://www.supermathapp.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-app-store"
                >
                  <ChevronRight size={20} />
                  {sm.learnMore}
                </a>
              </div>
            </div>
            <div className="supermath-visual animate-on-scroll animate-delay-2">
              <div className="supermath-video-wrapper">
                {/* <iframe
                  src="https://www.youtube-nocookie.com/embed/CXclda01big"
                  title="SuperMath - Imparare la matematica senza ansia"
                  frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                /> */}
                <div 
                  style={{
                    position: 'relative',
                    width: '100%',
                    paddingBottom: '56.25%', /* Mantiene il formato 16:9 */
                    height: 0,
                    backgroundColor: '#000',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}
                >
                  {!playVideo ? (
                    /* Copertina + Pulsante Play (nessuna chiamata di rete verso DoubleClick) */
                    <div
                      onClick={() => setPlayVideo(true)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: 'url(https://img.youtube.com/vi/CXclda01big/maxresdefault.jpg)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {/* Pulsante Play rosso */}
                      <div 
                        style={{
                          width: '68px',
                          height: '48px',
                          backgroundColor: '#FF0000',
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <div 
                          style={{
                            width: 0,
                            height: 0,
                            borderTop: '10px solid transparent',
                            borderBottom: '10px solid transparent',
                            borderLeft: '18px solid #FFF',
                            marginLeft: '4px'
                          }} 
                        />
                      </div>
                    </div>
                  ) : (
                    /* Iframe caricato SOLO dopo il click con il dominio nocookie */
                    <iframe
                      src="https://www.youtube-nocookie.com/embed/CXclda01big?autoplay=1"
                      title="SuperMath - Imparare la matematica senza ansia"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 0
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SUPERMATH TEACHER ── */}
      <section id="supermath-teacher" className="section supermath-teacher-section">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <span className="section-label">
              <GraduationCap size={16} />
              {smTeacher.badge}
            </span>
            <h2 className="supermath-title smt-title">{smTeacher.title}</h2>
            <p className="section-subtitle">{smTeacher.subtitle}</p>
          </div>

          <div className="smt-grid">
            <div className="smt-content animate-on-scroll">
              <p className="smt-desc">{smTeacher.desc}</p>

              <ul className="smt-features">
                <li><Check size={18} /> <span>{smTeacher.f1}</span></li>
                <li><Check size={18} /> <span>{smTeacher.f2}</span></li>
                <li><Check size={18} /> <span>{smTeacher.f3}</span></li>
                <li><Check size={18} /> <span>{smTeacher.f4}</span></li>
                <li><Check size={18} /> <span>{smTeacher.f5}</span></li>
              </ul>

              <div className="smt-actions">
                <a
                  href={smTeacher.appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-app-store"
                >
                  <PlayCircle size={20} />
                  {stores.appStore}
                </a>
                <a
                  href={smTeacher.playStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-app-store"
                >
                  <PlayCircle size={20} />
                  {stores.playStore}
                </a>
              </div>
            </div>

            <div className="smt-inclusive animate-on-scroll animate-delay-2">
              <div className="smt-inclusive-card">
                <div className="smt-inclusive-icon">
                  <Heart size={28} />
                </div>
                <h3>{smTeacher.inclusive}</h3>
                <p>{smTeacher.inclusiveDesc}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── LEONORA'S COLORS ── */}
      <section id="leonora" className="section leonora-section">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <span className="section-label">
              <Palette size={16} />
              {leo.badge}
            </span>
            <h2 className="supermath-title leonora-title">{leo.title}</h2>
            <p className="section-subtitle">{leo.subtitle}</p>
          </div>

          <div className="leonora-grid">
            <div className="leonora-content animate-on-scroll">
              <p className="leonora-desc">{leo.desc}</p>

              <ul className="leonora-features">
                <li><Check size={18} /> <span>{leo.f1}</span></li>
                <li><Check size={18} /> <span>{leo.f2}</span></li>
                <li><Check size={18} /> <span>{leo.f3}</span></li>
                <li><Check size={18} /> <span>{leo.f4}</span></li>
              </ul>

              <div className="leonora-actions">
                <a
                  href={leo.appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-app-store"
                >
                  <PlayCircle size={20} />
                  {stores.appStore}
                </a>
                <a
                  href={leo.playStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-app-store"
                >
                  <PlayCircle size={20} />
                  {stores.playStore}
                </a>
              </div>
            </div>

            <div className="leonora-visual animate-on-scroll animate-delay-2">
              <div className="leonora-color-circles">
                <div className="color-circle red"></div>
                <div className="color-circle yellow"></div>
                <div className="color-circle blue"></div>
                <div className="color-circle green"></div>
                <div className="color-circle orange"></div>
                <div className="color-circle purple"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── RESEARCH & DEVELOPMENT ── */}
      <section id="rnd" className="section rnd-section">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <span className="section-label">
              <FlaskConical size={16} />
              {rnd.badge}
            </span>
            <h2 className="section-title">
              {rnd.title}
              <br />
              <span className="hero-title-accent">{rnd.subtitle}</span>
            </h2>
          </div>

          <div className="rnd-grid">
            <div className="rnd-content animate-on-scroll">
              <p className="rnd-desc">{rnd.desc}</p>
            </div>

            <div className="rnd-visual animate-on-scroll animate-delay-2">
              <div className="rnd-icon-grid">
                <div className="rnd-icon-card">
                  <FlaskConical size={32} />
                </div>
                <div className="rnd-icon-card">
                  <Zap size={32} />
                </div>
                <div className="rnd-icon-card">
                  <Heart size={32} />
                </div>
                <div className="rnd-icon-card">
                  <Users size={32} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PREMIO CAMBIAMENTI ── */}
      <AwardSpotlight awards={awards} />

      {/* ── REVIEWS ── */}
      <section id="reviews" className="section reviews">
        <div className="section-inner">
          <div className="section-header animate-on-scroll">
            <div className="section-label">
              <Star size={14} />
              {rev.label}
            </div>
            <h2 className="section-title">{rev.title}</h2>
            <p className="section-subtitle">{rev.subtitle}</p>
          </div>
          <div className="reviews-grid">
            <ReviewCard text={rev.r1} author={rev.a1} initial={rev.a1?.[0]} delay={1} />
            <ReviewCard text={rev.r2} author={rev.a2} initial={rev.a2?.[0]} delay={2} />
            <ReviewCard text={rev.r3} author={rev.a3} initial={rev.a3?.[0]} delay={3} />
          </div>
        </div>
      </section>

      {/* ── TEACHERS PLATFORM (Coming soon — commented out) ── */}
      {/*
      <section id="teachers" className="section teachers">
        <div className="section-inner teachers-inner">
          <div className="teachers-layout">
            <div className="teachers-content animate-on-scroll">
              <div className="section-label">
                <Users size={14} />
                {teach.label}
              </div>
              <h2 className="teachers-title">
                {teach.titleLine1}
                <br />
                {teach.titleLine2}
              </h2>
              <p className="teachers-desc">{teach.desc}</p>
              <div className="teachers-features">
                <TeacherFeature text={teach.f1} />
                <TeacherFeature text={teach.f2} />
                <TeacherFeature text={teach.f3} />
                <TeacherFeature text={teach.f4} />
              </div>
            </div>
            <div className="animate-on-scroll animate-delay-2">
              <div className="teachers-form-card">
                <h3 className="form-title">{teach.formTitle}</h3>
                <p className="form-subtitle">{teach.formSubtitle}</p>
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="form-group">
                    <input
                      type="text"
                      className="form-input"
                      placeholder={teach.placeholderName}
                      id="teacher-name"
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="email"
                      className="form-input"
                      placeholder={teach.placeholderEmail}
                      id="teacher-email"
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      className="form-input"
                      placeholder={teach.placeholderSchool}
                      id="teacher-school"
                    />
                  </div>
                  <button type="submit" className="form-submit">
                    {teach.submit}
                  </button>
                  <p className="form-privacy">
                    <Lock size={12} />
                    {teach.privacy}
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      */}

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-top">
            <div>
              <a href="#top" className="nav-logo">
                <img src={`${process.env.PUBLIC_URL}/logo_40.png`} alt="EagleTap" className="nav-logo-img" />
                <span className="nav-logo-text" style={{ color: 'white' }}>
                  EagleTap
                </span>
              </a>
              <p className="footer-brand-desc">{foot.desc}</p>
            </div>
            <div className="footer-col">
              <h4 className="footer-col-title">{foot.products}</h4>
              <a href="#supermath" onClick={(e) => { e.preventDefault(); const el = document.getElementById('supermath'); if (el) { el.querySelectorAll('.animate-on-scroll').forEach(c => c.classList.add('visible')); el.scrollIntoView({ behavior: 'smooth' }); } }}>SuperMath</a>
              <a href="#supermath-teacher" onClick={(e) => { e.preventDefault(); const el = document.getElementById('supermath-teacher'); if (el) { el.querySelectorAll('.animate-on-scroll').forEach(c => c.classList.add('visible')); el.scrollIntoView({ behavior: 'smooth' }); } }}>SuperMath Teacher</a>
              <a href="#leonora" onClick={(e) => { e.preventDefault(); const el = document.getElementById('leonora'); if (el) { el.querySelectorAll('.animate-on-scroll').forEach(c => c.classList.add('visible')); el.scrollIntoView({ behavior: 'smooth' }); } }}>Leonora's Colors</a>
            </div>
            <div className="footer-col">
              <h4 className="footer-col-title">{foot.company}</h4>
              <a href="#values" onClick={(e) => scrollToSection(e, 'values')}>{foot.ourValues}</a>
              <a href="#reviews" onClick={(e) => scrollToSection(e, 'reviews')}>{foot.reviewsLink}</a>
              <a href="mailto:info@eagletap.com">{foot.contact}</a>
            </div>
            <div className="footer-col">
              <h4 className="footer-col-title">{foot.legal}</h4>
              {/* <a href="#/privacy">{foot.privacyPolicy}</a> */}
              {/* <a href="#/terms">{foot.terms}</a> */}
              <a href="#/site-privacy">{foot.sitePrivacy}</a>
              <a href="#/site-terms">{foot.siteTerms}</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="footer-copyright">
              © {new Date().getFullYear()} EagleTap Srl. {foot.copyright}
            </p>
            <div className="footer-badge">
              <ShieldCheck size={12} />
              Privacy by Design
            </div>
            <div className="footer-badge">
              <EyeOff size={12} />
              Cookie-free website. We don't track you.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

/* ============================
   Sub-components
   ============================ */

const ValueCard = ({ iconClass, icon, title, desc, delay }) => (
  <div className={`value-card animate-on-scroll animate-delay-${delay}`}>
    <div className={`value-icon ${iconClass}`}>{icon}</div>
    <h3 className="value-title">{title}</h3>
    <p className="value-desc">{desc}</p>
  </div>
);

const ReviewCard = ({ text, author, initial, delay }) => (
  <div className={`review-card animate-on-scroll animate-delay-${delay}`}>
    <div className="review-stars">
      {[...Array(5)].map((_, i) => (
        <Star key={i} fill="currentColor" size={16} />
      ))}
    </div>
    <p className="review-text">&ldquo;{text}&rdquo;</p>
    <div className="review-author">
      <div className="review-avatar">{initial}</div>
      <div className="review-author-info">
        <span className="review-author-name">{author}</span>
        <span className="review-author-source">Play Store</span>
      </div>
    </div>
  </div>
);

// const TeacherFeature = ({ text }) => (
//   <div className="teacher-feature">
//     <div className="teacher-feature-icon">
//       <Check size={14} />
//     </div>
//     <span className="teacher-feature-text">{text}</span>
//   </div>
// );

export default App;